/* eslint-disable max-lines */
import React from 'react'

import classNames from 'classnames'

import {
  BetaTesterWarningIcon,
  CommonAddIcon,
  CommonHorizontalArrow2Icon,
  CommonNotice2Icon,
  FollowNoContentIcon,
  PlaceNoticeLargeIcon,
  ReviewGuideImageIcon,
} from '@local/assets/icons'
import {
  CheckInNoReviewDarkImage,
  CheckInNoReviewLightImage,
  FeedNoContentDarkImage,
  FeedNoContentLightImage,
  FeedNoFollowerDarkImage,
  FeedNoFollowerLightImage,
  FeedNoLoginDarkImage,
  FeedNoLoginLightImage,
} from '@local/assets/images'

import useCheckinAccess from '@web/hooks/useCheckinAccess/useCheckinAccess'
import { darkModeVar } from '@web/hooks/useGlobalDetermineDarkMode'
import { useGoTo } from '@web/hooks/useGoTo'
import links from '@web/libs/constants/links'
import routes from '@web/libs/constants/routes'

import Clickable from '../Clickable'
import styles from './index.module.scss'
import TabPanelNoContent from './TabPanelNoContent'
import { NoContentTypes, TabPanelNoContentTypes } from './type'

interface Props {
  type: NoContentTypes
  clickHandler?: () => void
  clickCode?: string
  isFull?: boolean
  isMine?: boolean
}

const NoContentWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.wrapper}>{children}</div>
)

const NoContent = ({ type, clickHandler = () => {}, clickCode = '', isFull = false, isMine = false }: Props) => {
  const dark = darkModeVar()
  const { goTo, goToLogin, goToCheckIn } = useGoTo()
  const noFeedimageName = dark ? FeedNoContentDarkImage : FeedNoContentLightImage
  const noLoginimageName = dark ? FeedNoLoginDarkImage : FeedNoLoginLightImage
  const noFollowingimageName = dark ? FeedNoFollowerDarkImage : FeedNoFollowerLightImage
  const noReviewName = dark ? CheckInNoReviewDarkImage : CheckInNoReviewLightImage
  const { validateAccess } = useCheckinAccess({
    onSuccess: goToCheckIn,
    onNotLoggedIn: () => goToLogin({ url: `${links.MY_HOME}${routes.CHECKIN}` }),
  })

  if (type === NoContentTypes.DEFAULT)
    return (
      <NoContentWrapper>
        <div className={styles.tit_area}>내용이 없습니다.</div>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.ERROR)
    return isFull ? (
      <TabPanelNoContent type={TabPanelNoContentTypes.ERROR} onClick={clickHandler} isFull />
    ) : (
      <div className={styles.tab_panel_area}>
        <TabPanelNoContent type={TabPanelNoContentTypes.ERROR} onClick={clickHandler} />
      </div>
    )
  else if (type === NoContentTypes.VISIT)
    return (
      <div className={styles.tab_panel_area}>
        <TabPanelNoContent type={TabPanelNoContentTypes.VISIT} onClick={validateAccess} />
      </div>
    )
  else if (type === NoContentTypes.REVIEW)
    return (
      <div className={styles.tab_panel_area}>
        <TabPanelNoContent type={TabPanelNoContentTypes.REVIEW} />
      </div>
    )
  else if (type === NoContentTypes.VISIT_NOT_AVAILABLE)
    return (
      <NoContentWrapper>
        <>
          <div className={styles.tit_area}>
            방문한 장소를&nbsp;
            <strong>영수증으로 인증</strong>
            하고
            <br />
            플레이스 리뷰를 작성해보세요!
          </div>
          <div className={styles.btn_area}>
            <Clickable className={styles.btn} onClick={clickHandler}>
              플레이스 리뷰어 신청하기
            </Clickable>
          </div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.REVIEW_NO_PHOTO)
    return (
      <div className={classNames(styles.wrapper, styles.type_no_photo_review)}>
        <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>사진/영상 리뷰가 없습니다.</div>
      </div>
    )
  else if (type === NoContentTypes.REVIEW_NO_RECEIPT)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>영수증 인증 리뷰가 없습니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.REVIEW_NO_RESERVATION)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>네이버예약 리뷰가 없습니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.REVIEW_NO_DELIVERY)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>네이버주문 리뷰가 없습니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.REVIEW_GUEST)
    return (
      <div className={styles.tab_panel_area}>
        <TabPanelNoContent type={TabPanelNoContentTypes.REVIEW_OTHER} />
      </div>
    )
  else if (type === NoContentTypes.REVIEW_GUEST_NO_PHOTO)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>사진/영상 리뷰가 없습니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.REVIEW_NO_RESULT)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>리뷰가 없습니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.REVIEW_NOT_FOUND)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>작성자가 삭제하거나 유효하지 않은 리뷰입니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.REVIEW_NOT_AVAILABLE)
    return (
      <div className={styles.tab_panel_area}>
        <TabPanelNoContent type={TabPanelNoContentTypes.REVIEW} showJoinButton onClick={validateAccess} />
      </div>
    )
  else if (type === NoContentTypes.EMPTY_PUBLIC_PLACES)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>저장된 장소가 없습니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.NO_PLACES_IN_SCOPE)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>현재 범위에는 장소가 없습니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.REMOVED_FOLDER)
    return (
      <div className={classNames(styles.wrapper, styles.type_bookmark)}>
        <PlaceNoticeLargeIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>
          사용자가 리스트를 삭제하여
          <br />
          확인할 수 없습니다.
        </div>
      </div>
    )
  else if (type === NoContentTypes.PRIVATE_FOLDER)
    return (
      <div className={classNames(styles.wrapper, styles.type_bookmark)}>
        <PlaceNoticeLargeIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>
          사용자가 리스트를 비공개로 전환하여
          <br />
          확인할 수 없습니다.
        </div>
      </div>
    )
  else if (type === NoContentTypes.PUBLIC_FOLDER_EMPTY)
    return (
      <>
        <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>공개된 리스트가 없습니다.</div>
      </>
    )
  else if (type === NoContentTypes.NO_FOLDER_AUTHORITY)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>해당 리스트에 접근 권한이 없습니다.</div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.PLACE_FOLDERS_API_RESPONSE_ERROR)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>
            리스트 정보를 조회하는 중에
            <br />
            오류가 발생했습니다.
          </div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.PLACE)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>
            내 장소에 저장된 장소가 없습니다.
            <br />
            <strong>새로운 장소를 등록해보세요!</strong>
          </div>
          <div className={styles.btn_area}>
            <Clickable className={styles.btn} onClick={clickHandler} nClickCode={clickCode}>
              <CommonAddIcon className={styles.icon} aria-hidden="true" />
              장소 추가하기
            </Clickable>
          </div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.PLACE_NOT_LOGGED_IN)
    return (
      <div className={styles.tab_panel_area}>
        <TabPanelNoContent type={TabPanelNoContentTypes.BOOKMARK} />
      </div>
    )
  else if (type === NoContentTypes.LOCATION_SEARCH_GUIDE)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.location_text_area}>
            지역과 장소명을 함께 입력하면
            <br />
            장소를 더 쉽게 찾을 수 있습니다.
          </div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.LOCATION_SEARCH_EMPTY)
    return (
      <div className={classNames(styles.wrapper, styles.type_location)}>
        <BetaTesterWarningIcon className={classNames(styles.icon, styles.type_location)} aria-hidden="true" />
        <strong className={styles.tit_area}>검색결과가 없습니다.</strong>
        <div className={styles.txt_area}>
          다른 검색어를 입력하거나,
          <br />
          검색어의 철자가 정확한지 확인해 보세요.
        </div>
      </div>
    )
  else if (type === NoContentTypes.BOOKMARK_BUSINESS_EMPTY)
    return (
      <NoContentWrapper>
        <div className={styles.tit_area}>No content</div>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.UNAUTHORIZED_PLACE)
    return (
      <NoContentWrapper>
        <>
          <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
          <div className={styles.tit_area}>
            스마트플레이스 관리 권한이 없습니다.
            <br />
            로그아웃 후 관리 권한이 있는 아이디로
            <br />
            다시 로그인해주세요.
          </div>
          <div className={classNames(styles.btn_area, styles.type_logout)}>
            <Clickable className={styles.btn} onClick={clickHandler}>
              로그아웃
            </Clickable>
          </div>
        </>
      </NoContentWrapper>
    )
  else if (type === NoContentTypes.THEME_NO_REVIEW_SORT)
    return (
      <div className={classNames(styles.wrapper, styles.type_theme)}>
        <ReviewGuideImageIcon className={styles.icon_photo} aria-hidden="true" />
        <div className={styles.tit_area}>리뷰가 없습니다.</div>
      </div>
    )
  else if (type === NoContentTypes.THEME_NO_REVIEW || type === NoContentTypes.THEME_NO_PHOTO_REVIEW)
    return (
      <div className={classNames(styles.wrapper, styles.type_theme)}>
        <ReviewGuideImageIcon className={styles.icon_photo} aria-hidden="true" />
        {type === NoContentTypes.THEME_NO_REVIEW ? (
          <div className={styles.tit_area}>
            테마에 담을 수 있는 리뷰가 없습니다.
            <br /> 텍스트가 있는 리뷰만 테마에 담을 수 있습니다.
          </div>
        ) : (
          <div className={styles.tit_area}>
            테마에 담을 수 있는 <strong>사진 리뷰</strong>가 없습니다.
            <br /> 텍스트가 있는 <strong>사진 리뷰</strong>만 테마에 담을 수 있습니다.
          </div>
        )}
        <div className={styles.btn_area}>
          <Clickable
            className={styles.btn}
            onClick={() => {
              goTo(routes.HOME_MY_REVIEW)
            }}
          >
            리뷰 쓰러가기
            <CommonHorizontalArrow2Icon className={styles.icon} aria-hidden="true" />
          </Clickable>
        </div>
      </div>
    )
  else if (type === NoContentTypes.THEME_REMOVED)
    return (
      <div className={classNames(styles.wrapper, styles.type_theme)}>
        <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>사용자가 테마를 비공개 전환 또는 삭제하여 확인할 수 없습니다. </div>
      </div>
    )
  else if (type === NoContentTypes.FOLLOWERS || type === NoContentTypes.FOLLOWING)
    return (
      <div className={classNames(styles.wrapper, styles.type_follow)}>
        <FollowNoContentIcon className={styles.icon_follow} aria-hidden="true" />
        <p className={styles.tit_area}>
          {type === NoContentTypes.FOLLOWERS ? '아직 팔로워가 없습니다' : '아직 팔로잉하는 리뷰어가 없습니다'}
        </p>
      </div>
    )
  else if (type === NoContentTypes.FEED)
    return (
      <div className={classNames(styles.no_content_area_feed, styles.type_update_review)}>
        <img src={noFeedimageName.src} className={styles.img} width="159" height="60" alt="" />
        <strong className={styles.tit}>새로 업데이트된 콘텐츠가 없네요!</strong>
        <div className={styles.txt}>피드에서는 최근 업로드된 리뷰만 보여드려요.</div>
      </div>
    )
  else if (type === NoContentTypes.FEED_FOLLOWER)
    return (
      <div className={styles.no_content_area_feed}>
        <img src={noFollowingimageName.src} className={styles.img} width="99" height="68" alt="" />
        <strong className={styles.tit}>맘에 드는 리뷰어 팔로우해볼까요?</strong>
        <div className={styles.txt}>여기서 최신 리뷰를 모아볼 수 있어요</div>
      </div>
    )
  else if (type === NoContentTypes.FEED_LOGIN)
    return (
      <div className={styles.no_content_area_feed}>
        <img src={noLoginimageName.src} className={styles.img} width="99" height="73" alt="" />
        <strong className={styles.tit}>로그인하고 리뷰어를 팔로우하세요!</strong>
        <div className={styles.txt}>여기서 최신 리뷰를 모아볼 수 있어요</div>
      </div>
    )
  else if (type === NoContentTypes.FEED_FOLLOWING)
    return (
      <div className={styles.no_feed_content}>
        <img src={noFollowingimageName.src} className={styles.img} width="99" height="68" alt="" />
        <strong className={styles.tit}>팔로잉하는 리뷰어의 소식을 다 확인했어요!</strong>
        <div className={styles.txt}>맘에 드는 리뷰어를 더 팔로우해보세요.</div>
      </div>
    )
  else if (type === NoContentTypes.FEED_FOLLOWING_REVIEW)
    return (
      <div className={classNames(styles.no_feed_content)}>
        <img src={noFeedimageName.src} className={styles.img} width="159" height="60" alt="" />
        <strong className={styles.tit}>표시할 수 있는 리뷰어의 소식이 없어요!</strong>
        <div className={styles.txt}>맘에 드는 리뷰어를 더 팔로우해보세요.</div>
      </div>
    )
  else if (type === NoContentTypes.BLOCK)
    return (
      <div className={classNames(styles.wrapper, styles.type_block)}>
        <div className={styles.tit_area}>아직 차단한 사용자가 없습니다</div>
      </div>
    )
  else if (type === NoContentTypes.BOOKMARK_LIST_EMPTY)
    return (
      <div className={classNames(styles.wrapper, styles.type_bookmark)}>
        <PlaceNoticeLargeIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>저장된 장소가 없습니다.</div>
      </div>
    )
  else if (type === NoContentTypes.BOOKMARK_AROUND_EMPTY)
    return (
      <div className={classNames(styles.wrapper, styles.type_bookmark)}>
        <PlaceNoticeLargeIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>조건에 해당하는 장소가 없습니다.</div>
        <div className={styles.txt_area}>지도를 움직이거나, 필터를 변경해 보세요.</div>
      </div>
    )
  // 임시 게시중단
  else if (type === NoContentTypes.TEMPORARY_SUSPENSION_NOTICE)
    return (
      <div className={classNames(styles.wrapper, styles.type_reported)}>
        {/* TODO: 아이콘 디자인 확인 후 교체예정 */}
        <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>
          본 게시물은 정보통신망 이용촉진 및 정보보호 등에 관한 법률 제 44조 2항을 준수하기 위해 다른 이용자의 요청으로
          임시 게시중단 되었습니다.
          {/* 본인뷰인 경우 노출 */}
          {isMine && (
            <Clickable href={links.TEMPORARY_SUSPENSION_NOTICE} className={styles.link}>
              자세히
            </Clickable>
          )}
        </div>
      </div>
    )
  // 게시중단
  else if (type === NoContentTypes.PERMANENT_SUSPENSION_NOTICE)
    return (
      <div className={classNames(styles.wrapper, styles.type_reported)}>
        {/* TODO: 아이콘 디자인 확인 후 교체예정 */}
        <BetaTesterWarningIcon className={styles.icon} aria-hidden="true" />
        <div className={styles.tit_area}>
          본 게시물은 정보통신망 이용촉진 및 정보보호 등에 관한 법률 제 44조 2항을 준수하기 위해 다른 이용자의 요청으로
          삭제되었습니다.
        </div>
      </div>
    )
  else if (type === NoContentTypes.NO_LIST_FOR_CHECK_IN || type === NoContentTypes.NO_PAYMENTS_FOR_CHECK_IN)
    return (
      <div
        className={classNames(styles.no_content_area_review, {
          [styles.type_only]: type === NoContentTypes.NO_LIST_FOR_CHECK_IN,
        })}
      >
        <img src={noReviewName.src} className={styles.img} width="131" height="116" alt="" />
        <div className={styles.tit_area}>리뷰 쓸 수 있는 내역이 없어요!</div>
        {type === NoContentTypes.NO_LIST_FOR_CHECK_IN && (
          <div className={styles.txt_area}>상단의 인증 방법을 선택해 리뷰를 써보세요.</div>
        )}
        {type === NoContentTypes.NO_PAYMENTS_FOR_CHECK_IN && (
          <div className={styles.txt_area}>새로 불러오기 버튼을 눌러 최신 내역을 확인해 보세요.</div>
        )}
      </div>
    )
  else if (type === NoContentTypes.NO_REVIEW_IN_REVIEW_TAB || type === NoContentTypes.NO_REVIEW_IN_REVIEW_TAB_TYPE_MINE)
    return (
      <div className={classNames(styles.no_content_area_review_tab)}>
        <div className={styles.nocontent}>
          <CommonNotice2Icon aria-hidden="true" className={styles.icon_notice} />
          <div className={styles.tit_area}>작성한 리뷰가 없습니다.</div>
          {type === NoContentTypes.NO_REVIEW_IN_REVIEW_TAB_TYPE_MINE && (
            <>
              <div className={styles.txt_area}>나의 발자취를 소중한 기록으로 남겨보세요.</div>
              <div className={styles.btn_area}>
                <Clickable className={styles.btn} onClick={validateAccess}>
                  리뷰 쓰기
                </Clickable>
              </div>
            </>
          )}
        </div>
      </div>
    )
  else if (type === NoContentTypes.CLIP_NOT_FOUND)
    return (
      <div className={classNames(styles.wrapper, styles.type_location)}>
        <BetaTesterWarningIcon className={classNames(styles.icon, styles.type_location)} aria-hidden="true" />
        <strong className={styles.tit_area}>주소를 다시 한 번 확인해 주세요!</strong>
        <div className={styles.txt_area}>이 게시물은 삭제되었거나 존재하지 않습니다.</div>
      </div>
    )
  return <div className={styles.wrapper}>NO_CONTENT</div>
}

export default NoContent
